import * as LabelPrimitive from '@radix-ui/react-label';
import { Eye, EyeOff } from 'lucide-react';
import { useState, type ComponentProps } from 'react';
import { cn } from '@/lib/cn';

export function Input({ className, ...props }: ComponentProps<'input'>) {
  return (
    <input
      className={cn(
        // 44px no celular, 36 no monitor — a mesma razão do botão.
        'flex h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-3 py-1 text-sm md:h-9',
        'placeholder:text-(--color-content-muted) disabled:cursor-not-allowed disabled:opacity-50',
        'aria-[invalid=true]:border-(--color-state-danger)',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Campo de senha com o olho de mostrar o que se digitou.
 *
 * Existe como componente, e não como marcação repetida, porque a parte que importa é
 * invisível: o rótulo diz o que o botão **faz** e o `aria-pressed` diz em que estado ele
 * está — sem o segundo, quem usa leitor de tela não teria como saber que a senha está à
 * mostra na tela. Duas cópias disso divergem na primeira pressa.
 *
 * O botão fica **ao lado** do texto, com o campo reservando o espaço dele: por cima, o
 * olho esconderia justamente o fim do que a pessoa está conferindo.
 */
export function PasswordInput({
  id,
  className,
  ...props
}: Omit<ComponentProps<'input'>, 'type'> & { id: string }) {
  const [visivel, setVisivel] = useState(false);

  return (
    <div className="relative">
      <Input
        id={id}
        type={visivel ? 'text' : 'password'}
        className={cn('pr-11', className)}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisivel((atual) => !atual)}
        aria-label={visivel ? 'Ocultar senha' : 'Mostrar senha'}
        aria-pressed={visivel}
        aria-controls={id}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-md text-(--color-content-muted) hover:text-(--color-content)"
      >
        {visivel ? (
          <EyeOff className="size-4" aria-hidden="true" />
        ) : (
          <Eye className="size-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

export function Textarea({ className, ...props }: ComponentProps<'textarea'>) {
  return (
    <textarea
      className={cn(
        'flex min-h-20 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-3 py-2 text-sm',
        'placeholder:text-(--color-content-muted) disabled:cursor-not-allowed disabled:opacity-50',
        'aria-[invalid=true]:border-(--color-state-danger)',
        className,
      )}
      {...props}
    />
  );
}

export function Label({ className, ...props }: ComponentProps<typeof LabelPrimitive.Root>) {
  return (
    <LabelPrimitive.Root className={cn('text-sm font-medium leading-none', className)} {...props} />
  );
}

export interface FieldProps {
  label: string;
  htmlFor: string;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean;
  children: React.ReactNode;
}

/** Campo com rótulo, dica e erro — mensagem de erro sempre associada por `aria-describedby`. */
export function Field({ label, htmlFor, error, hint, required, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>
        {label}
        {required === true && (
          <span className="ml-0.5 text-(--color-state-danger)" aria-hidden="true">
            *
          </span>
        )}
      </Label>
      {children}
      {hint !== undefined && error === undefined && (
        <p id={`${htmlFor}-hint`} className="text-xs text-(--color-content-muted)">
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={`${htmlFor}-error`} role="alert" className="text-xs text-(--color-state-danger)">
          {error}
        </p>
      )}
    </div>
  );
}
